import {
  ChevronDown,
  ChevronUp,
  Edit,
  Eye,
  Filter,
  Search,
  X,
  Grid3X3,
  List,
  Columns,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

import { useOrdersStore } from '../stores/ordersStore';
import { ordersService } from '../services/ordersService';
import { productsService } from '../services/productsService';
import { PromotionStatusBadge } from './components/PromotionStatusBadge';
import { Order, OrderStatus } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import AddressField from '../visitors/AddressField';
import CustomerReferenceField from '../visitors/CustomerReferenceField';
import NbItemsField from './NbItemsField';
import ProductSummaryDialog from './ProductSummaryDialog';
import { ModernReleaseAllTablesButton } from './components/ModernReleaseAllTablesButton';
import { MobileOrderCard } from './components/MobileOrderCard';
import { useTableStatusStore } from '../stores/useTableStatusStore';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
// Feature Wizard imports - DISABLED
// import FeatureWizard from '../components/FeatureWizard';
// import { useFeatureWizard } from '../hooks/useFeatureWizard';
// import { getModernOrderListWizardSteps } from '../wizards/modernOrderListWizard';
import { QuickDateRange } from '../components/common/QuickDateRange';
import { DateRangePreset } from '../utils/dateRangeUtils';

const calculateOrderTotal = (order: Order): number => {
  if (order.total) {
    return order.total;
  }

  if (order.items && Array.isArray(order.items)) {
    return order.items.reduce((sum, item) => {
      const price = typeof item.price === 'number' ? item.price : 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
      return sum + price * quantity;
    }, 0);
  }

  return 0;
};

const calculateOrderSubtotal = (order: Order): number => {
  if (order.totalExTaxes) {
    return order.totalExTaxes;
  }

  if (order.items && Array.isArray(order.items)) {
    return order.items.reduce((sum, item) => {
      const price = typeof item.price === 'number' ? item.price : 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
      return sum + price * quantity;
    }, 0);
  }

  return 0;
};

const calculateOrderDiscount = (order: Order): number => {
  const subtotal = calculateOrderSubtotal(order);
  const taxes = order.taxes || 0;
  const deliveryFees = order.deliveryFees || 0;
  const total = calculateOrderTotal(order);

  const totalBeforeDiscount = subtotal + taxes + deliveryFees;
  const discount =
    totalBeforeDiscount > total ? totalBeforeDiscount - total : 0;

  return discount;
};

const formatDate = (date: Date | string | null | undefined) => {
  if (!date) {
    return '-';
  }

  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return '-';
    }

    return new Intl.DateTimeFormat('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', date, error);
    return '-';
  }
};

// Column configuration
const COLUMN_STORAGE_KEY = 'neko-order-list-columns';
const DEFAULT_VISIBLE_COLUMNS: Record<string, boolean> = {
  date: true,
  reference: true,
  customer: true,
  address: true,
  status: true,
  items: true,
  note: true,
  subtotal: true,
  discount: true,
  total: true,
  returned: true,
  actions: true, // Always visible
};

const COLUMN_KEYS = [
  { key: 'date', labelKey: 'pos.orders.fields.date' },
  { key: 'reference', labelKey: 'pos.orders.fields.reference' },
  { key: 'customer', labelKey: 'pos.orders.fields.customer' },
  { key: 'address', labelKey: 'pos.orders.fields.address' },
  { key: 'status', labelKey: 'pos.orders.fields.status' },
  { key: 'items', labelKey: 'pos.orders.fields.items' },
  { key: 'note', labelKey: 'pos.orders.fields.note' },
  { key: 'subtotal', labelKey: 'pos.orders.fields.subtotal' },
  { key: 'discount', labelKey: 'pos.orders.fields.discount' },
  { key: 'total', labelKey: 'pos.orders.fields.total' },
  { key: 'returned', labelKey: 'pos.orders.fields.returned' },
];

const ModernOrderList: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrencyFormat();

  const { releaseTable } = useTableStatusStore();

  console.log('ModernOrderList v2 rendered - DEBUG CHECK');

  // Feature Wizard - DISABLED
  // const wizardConfig = {
  //   id: 'modernOrderList',
  //   title: translate('pos.wizards.modernOrderList.welcome.title'),
  //   description: translate(
  //     'pos.wizards.modernOrderList.welcome.description'
  //   ),
  //   showOnRoute: '/orders',
  //   autoShow: false,
  // };
  // const {
  //   isOpen: isWizardOpen,
  //   openWizard,
  //   closeWizard,
  //   completeWizard,
  //   skipWizard,
  //   hasSeenFeature,
  // } = useFeatureWizard(wizardConfig);

  // Feature Wizard - DISABLED (no longer needed)
  const hasSeenFeature = true; // Always consider as seen to prevent any wizard logic
  const openWizard = () => {}; // No-op function
  const {
    orders,
    loading,
    error,
    filters,
    pagination,
    sort,
    fetchOrders,
    updateOrderStatus,
    setFilters,
    clearFilters,
    setPagination,
    setSort,
    clearError,
  } = useOrdersStore();

  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [suborderCounts, setSuborderCounts] = useState<Record<string, number>>(
    {}
  );
  const [suborderTotals, setSuborderTotals] = useState<Record<string, number>>(
    {}
  );

  // Sync filterForm with current filters when modal opens
  useEffect(() => {
    if (showFilterModal) {
      setFilterForm({
        customerId: filters.customerId || '',
        dateFrom: filters.dateFrom
          ? (() => {
              // Parse ISO string and extract YYYY-MM-DD in UTC to avoid timezone shifts
              // Extract date part directly from ISO string if possible, otherwise parse
              if (
                typeof filters.dateFrom === 'string' &&
                filters.dateFrom.includes('T')
              ) {
                // ISO string format: extract YYYY-MM-DD part
                return filters.dateFrom.split('T')[0];
              }
              const date = new Date(filters.dateFrom);
              const year = date.getUTCFullYear();
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const day = String(date.getUTCDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            })()
          : '',
        dateTo: filters.dateTo
          ? (() => {
              // Parse ISO string and extract YYYY-MM-DD in UTC to avoid timezone shifts
              // Extract date part directly from ISO string if possible, otherwise parse
              if (
                typeof filters.dateTo === 'string' &&
                filters.dateTo.includes('T')
              ) {
                // ISO string format: extract YYYY-MM-DD part
                return filters.dateTo.split('T')[0];
              }
              const date = new Date(filters.dateTo);
              const year = date.getUTCFullYear();
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const day = String(date.getUTCDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            })()
          : '',
        totalGte: filters.totalGte?.toString() || '',
        returned:
          filters.returned !== undefined ? filters.returned.toString() : '',
        categoryId: filters.categoryId || '',
        productIds: filters.productIds || [],
        sourcePlatform: filters.sourcePlatform || '',
      });
    }
  }, [showFilterModal, filters]);

  // Load products when filter modal opens
  useEffect(() => {
    if (showFilterModal) {
      const loadProducts = async () => {
        // Only load if we don't have products yet
        if (products.length === 0) {
          setLoadingProducts(true);
          try {
            const response = await productsService.getProducts({
              page: 1,
              perPage: 1000,
              sort: JSON.stringify(['name', 'ASC']),
            });
            const productsData = Array.isArray(response) ? response : [];
            setProducts(productsData);
          } catch (error) {
            console.error('Error loading products:', error);
          } finally {
            setLoadingProducts(false);
          }
        }
      };
      loadProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilterModal]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [viewMode, setViewMode] = useState<'auto' | 'table' | 'cards'>('auto');
  const [categories, setCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('custom');
  const [filterForm, setFilterForm] = useState({
    customerId: '',
    dateFrom: '',
    dateTo: '',
    totalGte: '',
    returned: '',
    categoryId: '',
    productIds: [] as string[],
    sourcePlatform: '' as '' | 'all' | 'grab' | 'shopee' | 'xanhsm' | 'internal',
  });

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    () => {
      try {
        const saved = localStorage.getItem(COLUMN_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Merge with defaults to ensure all columns exist
          return { ...DEFAULT_VISIBLE_COLUMNS, ...parsed };
        }
      } catch (error) {
        console.error('Error loading column preferences:', error);
      }
      return DEFAULT_VISIBLE_COLUMNS;
    }
  );

  // Save column visibility to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibleColumns));
    } catch (error) {
      console.error('Error saving column preferences:', error);
    }
  }, [visibleColumns]);

  // Toggle column visibility
  const toggleColumn = useCallback((columnKey: string) => {
    if (columnKey === 'actions') return; // Actions column is always visible
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  }, []);

  // Reset columns to default
  const resetColumns = useCallback(() => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  }, []);

  // Calculate visible column count for colSpan
  const getVisibleColumnCount = useCallback(() => {
    let count = 0;
    if (visibleColumns.date) count++;
    if (visibleColumns.reference) count++;
    if (visibleColumns.customer) count++;
    if (visibleColumns.address) count++;
    if (visibleColumns.status) count++;
    if (visibleColumns.items) count++;
    if (visibleColumns.note) count++;
    if (visibleColumns.subtotal) count++;
    if (visibleColumns.discount) count++;
    if (visibleColumns.total) count++;
    if (filters.status === 'delivered' && visibleColumns.returned) count++;
    if (visibleColumns.actions) count++;
    return count;
  }, [visibleColumns, filters.status]);

  // Check if any filters are active
  const hasActiveFilters =
    filters.customerId ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.totalGte ||
    filters.returned !== undefined ||
    filters.categoryId ||
    (filters.productIds && filters.productIds.length > 0);

  // Sync search value with filters
  useEffect(() => {
    if (filters.search !== searchValue) {
      setSearchValue(filters.search || '');
    }
  }, [filters.search]);
  const isInitialMount = useRef(true);

  // Initial load
  useEffect(() => {
    fetchOrders();
    // Load categories with pagination (fetch all for dropdown)
    const loadCategories = async () => {
      try {
        const categoriesData = await productsService.getProductCategories({
          page: 1,
          perPage: 1000, // Fetch all categories for dropdown
        });
        const categories = Array.isArray(categoriesData) ? categoriesData : [];
        setCategories(categories);
        console.log(
          `✅ Loaded ${categories.length} categories (total: ${
            (categoriesData as any).__total || categories.length
          })`
        );
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []); // Only run on mount

  // Fetch suborder counts and totals for parent orders
  useEffect(() => {
    const fetchSuborderInfo = async () => {
      if (orders.length === 0) return;

      // Find all parent orders (orders without parentOrderId)
      const parentOrders = orders.filter((o) => !(o as any).parentOrderId);
      if (parentOrders.length === 0) {
        setSuborderCounts({});
        setSuborderTotals({});
        return;
      }

      try {
        // Fetch all orders with pagination to find suborders
        const counts: Record<string, number> = {};
        const totals: Record<string, number> = {};
        let page = 1;
        const perPage = 1000;
        let hasMore = true;
        const parentOrderIds = new Set(
          parentOrders.map((o) => o.id.toString())
        );

        while (hasMore) {
          const ordersResponse = await ordersService.getOrders({
            pagination: { page, perPage },
            sort: { field: 'date', order: 'DESC' },
            filter: {},
          });

          // Count suborders and calculate totals for each parent
          (ordersResponse.data || []).forEach((suborder: any) => {
            if (
              suborder.parentOrderId &&
              parentOrderIds.has(suborder.parentOrderId)
            ) {
              const parentId = suborder.parentOrderId;
              counts[parentId] = (counts[parentId] || 0) + 1;
              totals[parentId] =
                (totals[parentId] || 0) + (Number(suborder.total) || 0);
            }
          });

          // Check if we've fetched all orders
          if (
            ordersResponse.data.length < perPage ||
            page * perPage >= (ordersResponse.total || 0)
          ) {
            hasMore = false;
          } else {
            page++;
          }
        }

        setSuborderCounts(counts);
        setSuborderTotals(totals);
      } catch (error) {
        console.error('Error fetching suborder info:', error);
      }
    };

    fetchSuborderInfo();
  }, [orders]);

  // Handle data fetching when filters, pagination, or sort changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    console.log('useEffect triggered - fetching orders with:', {
      filters,
      pagination,
      sort,
    });
    fetchOrders();
  }, [
    filters.status,
    filters.search,
    filters.categoryId,
    filters.productIds,
    filters.customerId,
    filters.dateFrom,
    filters.dateTo,
    filters.totalGte,
    filters.returned,
    pagination.page,
    pagination.perPage,
    sort.field,
    sort.order,
  ]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== filters.search) {
        console.log('Search value changed:', searchValue);
        console.log('Current filters before search:', filters);
        setFilters({ search: searchValue });
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchValue, filters.search, setFilters]);

  const handleSearch = (value: string) => {
    console.log('handleSearch called with value:', value);
    setSearchValue(value);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilterForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProductToggle = (productId: string) => {
    setFilterForm((prev) => {
      const currentIds = prev.productIds || [];
      const newIds = currentIds.includes(productId)
        ? currentIds.filter((id) => id !== productId)
        : [...currentIds, productId];
      return { ...prev, productIds: newIds };
    });
  };

  const handleApplyFilters = () => {
    const newFilters: any = {};

    if (filterForm.customerId) newFilters.customerId = filterForm.customerId;

    // Format dates properly for API
    // Date input returns YYYY-MM-DD format in local timezone
    // We need to create dates at start/end of day in LOCAL timezone, then convert to UTC for database
    if (filterForm.dateFrom) {
      // Parse the date string (YYYY-MM-DD) and create local date at midnight
      const [year, month, day] = filterForm.dateFrom.split('-').map(Number);
      // Create date in local timezone at start of day (00:00:00 local time)
      const startDate = new Date(year, month - 1, day, 0, 0, 0, 0);
      newFilters.dateFrom = startDate.toISOString();
      console.log('🔍 Date From filter:', {
        input: filterForm.dateFrom,
        localDate: startDate.toString(),
        parsed: startDate.toISOString(),
        year,
        month,
        day,
        utcDate: startDate.toUTCString(),
      });
    }
    if (filterForm.dateTo) {
      // Parse the date string (YYYY-MM-DD) and create local date at end of day
      const [year, month, day] = filterForm.dateTo.split('-').map(Number);
      // Create date in local timezone at end of day (23:59:59.999 local time)
      const endDate = new Date(year, month - 1, day, 23, 59, 59, 999);
      newFilters.dateTo = endDate.toISOString();
      console.log('🔍 Date To filter:', {
        input: filterForm.dateTo,
        localDate: endDate.toString(),
        parsed: endDate.toISOString(),
        year,
        month,
        day,
        utcDate: endDate.toUTCString(),
      });
    }

    if (filterForm.totalGte)
      newFilters.totalGte = parseFloat(filterForm.totalGte);
    if (filterForm.returned !== '')
      newFilters.returned = filterForm.returned === 'true';
    if (filterForm.categoryId && filterForm.categoryId !== 'all')
      newFilters.categoryId = filterForm.categoryId;
    if (filterForm.productIds && filterForm.productIds.length > 0)
      newFilters.productIds = filterForm.productIds;
    const validPlatforms = ['grab', 'shopee', 'xanhsm', 'internal'] as const;
    if (filterForm.sourcePlatform && 
        filterForm.sourcePlatform !== 'all' &&
        validPlatforms.includes(filterForm.sourcePlatform as any)) {
      newFilters.sourcePlatform = filterForm.sourcePlatform;
    }

    console.log('Applying filters:', newFilters);
    setFilters(newFilters);
    setShowFilterModal(false);
    notify('Filters applied successfully', { type: 'success' });
  };

  const handleClearFilters = () => {
    setFilterForm({
      customerId: '',
      dateFrom: '',
      dateTo: '',
      totalGte: '',
      returned: '',
      categoryId: '',
      productIds: [],
      sourcePlatform: '',
    });
    setSelectedPreset('custom');
    console.log('Clearing all filters');
    clearFilters();
    setShowFilterModal(false);
    notify('Filters cleared', { type: 'info' });
  };

  const handleStatusFilter = useCallback(
    (status: OrderStatus) => {
      setFilters({ status });
    },
    [setFilters]
  );

  const handleSort = useCallback(
    (field: string) => {
      console.log('Sort clicked:', field, 'Current sort:', sort);
      const newOrder =
        sort.field === field && sort.order === 'ASC' ? 'DESC' : 'ASC';
      console.log('New sort order:', newOrder);
      setSort({ field, order: newOrder });
    },
    [sort.field, sort.order, setSort]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPagination({ page: newPage });
    },
    [setPagination]
  );

  // Check if order can be updated
  const canUpdateOrder = useCallback((order: Order) => {
    return (
      !order.paid &&
      order.status !== 'cancelled' &&
      order.status !== 'delivered'
    );
  }, []);

  // Handle status update
  const handleStatusUpdate = useCallback(
    async (orderId: string, newStatus: OrderStatus) => {
      try {
        console.log(`handleStatusUpdate called for ${orderId} to ${newStatus}`);

        if (newStatus === 'delivered') {
          console.log('Calling ordersService.markAsDelivered directly');
          await ordersService.markAsDelivered(orderId);
        } else {
          await updateOrderStatus(orderId, newStatus);
        }

        notify('Order status updated successfully', { type: 'success' });

        // Handle table release for dine-in orders
        const order = orders.find((o) => o.id === orderId);
        if (order && order.tableNumber && newStatus === 'delivered') {
          await releaseTable(order.tableNumber);
        }

        // Refresh orders list
        await fetchOrders();
      } catch (error) {
        console.error('Error updating order status:', error);
        notify('Error updating order status', { type: 'error' });
      }
    },
    [updateOrderStatus, notify, orders, releaseTable, fetchOrders]
  );

  // Handle edit order
  const handleEditOrder = useCallback(
    (orderId: string) => {
      navigate(`/orders/${orderId}/edit`);
    },
    [navigate]
  );

  // Handle view order
  const handleViewOrder = useCallback(
    (orderId: string) => {
      navigate(`/orders/${orderId}/show`);
    },
    [navigate]
  );

  // Handle product summary dialog
  const handleProductSummaryClick = useCallback((order: Order) => {
    setSelectedOrder(order);
    setShowProductDialog(true);
  }, []);

  const handleCloseProductDialog = useCallback(() => {
    setShowProductDialog(false);
    setSelectedOrder(null);
  }, []);

  const getSortIcon = (field: string) => {
    if (sort.field !== field) return null;
    return sort.order === 'ASC' ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const tabs: { id: OrderStatus; name: string }[] = [
    { id: 'ordered', name: 'pos.orders.tabs.ordered' },
    { id: 'ready', name: 'pos.orders.tabs.ready' },
    { id: 'delivered', name: 'pos.orders.tabs.delivered' },
    { id: 'cancelled', name: 'pos.orders.tabs.cancelled' },
  ];

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={clearError}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      {/* Header Section */}
      <div className="bg-card border-b border-border shadow-sm">
        {/* Header */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between bg-card p-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold tracking-tight text-card-foreground">
              {translate('pos.orders.modern.title')}
            </h1>
            <p className="text-sm lg:text-base text-muted-foreground">
              {translate('pos.orders.modern.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 w-full lg:w-auto">
            <Button
              className={`px-4 py-2 rounded-md ${
                hasActiveFilters
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              onClick={() => setShowFilterModal(true)}
            >
              <Filter className="h-4 w-4 mr-2" />
              {translate('pos.orders.modern.filters')}
              {hasActiveFilters && (
                <Badge className="ml-2 bg-white text-orange-600 text-xs">
                  {
                    Object.keys(filters).filter(
                      (key) =>
                        key !== 'status' &&
                        key !== 'search' &&
                        filters[key as keyof typeof filters]
                    ).length
                  }
                </Badge>
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="outline"
                className="border-orange-300 text-orange-600 hover:bg-orange-50 px-3 py-2 rounded-md"
                onClick={handleClearFilters}
                title="Clear all filters"
              >
                <X className="h-4 w-4 mr-1" />
                {translate('pos.common.action.clear')}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-border text-foreground hover:bg-muted px-4 py-2 rounded-md"
                >
                  <Columns className="h-4 w-4 mr-2" />
                  {translate('pos.orders.modern.columns', 'Columns')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  {translate(
                    'pos.orders.modern.showColumns',
                    'Show Columns'
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMN_KEYS.map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={visibleColumns[column.key] ?? true}
                    onCheckedChange={() => toggleColumn(column.key)}
                    disabled={column.key === 'actions'}
                  >
                    {translate(column.labelKey)}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={visibleColumns.actions ?? true}
                  disabled
                >
                  {translate('pos.common.action.actions')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    resetColumns();
                  }}
                >
                  {translate(
                    'pos.orders.modern.resetColumns',
                    'Reset to Default'
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              className="border-border text-foreground hover:bg-muted px-4 py-2 rounded-md"
              onClick={() => {
                // TODO: Implement export functionality
                notify('Export functionality coming soon', { type: 'info' });
              }}
            >
              {translate('pos.orders.modern.export')}
            </Button>
            <Button
              variant="default"
              className="px-4 py-2 rounded-md"
              onClick={() => {
                console.log('Navigating to create order...');
                navigate('/orders/create');
              }}
            >
              {translate('pos.common.action.create')}
            </Button>
            <ModernReleaseAllTablesButton />
            {!hasSeenFeature && (
              <Button
                onClick={openWizard}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-200 dark:hover:bg-blue-800"
              >
                <Eye className="h-4 w-4" />
                {translate('pos.common.action.help')}
              </Button>
            )}

            {/* View Toggle - Only show on larger screens */}
            <div className="hidden md:flex items-center gap-1 border border-border rounded-md">
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="px-3 py-1"
                title="Table View"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
                className="px-3 py-1"
                title="Card View"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'auto' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('auto')}
                className="px-3 py-1"
                title="Auto (Responsive)"
              >
                <span className="text-xs">A</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="px-4 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">
                {translate('pos.orders.modern.activeFilters')}:
              </span>
              {filters.customerId && (
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-800"
                >
                  Customer: {filters.customerId}
                </Badge>
              )}
              {filters.dateFrom && (
                <Badge
                  variant="secondary"
                  className="bg-green-100 text-green-800"
                >
                  From:{' '}
                  {(() => {
                    // Extract date directly from ISO string to avoid timezone issues
                    const dateStr = filters.dateFrom;
                    if (typeof dateStr === 'string' && dateStr.includes('T')) {
                      // ISO format: extract YYYY-MM-DD and parse
                      const [year, month, day] = dateStr
                        .split('T')[0]
                        .split('-')
                        .map(Number);
                      return `${String(day).padStart(2, '0')}/${String(
                        month
                      ).padStart(2, '0')}/${year}`;
                    }
                    // Fallback: parse as Date and use UTC
                    const date = new Date(dateStr);
                    const day = String(date.getUTCDate()).padStart(2, '0');
                    const month = String(date.getUTCMonth() + 1).padStart(
                      2,
                      '0'
                    );
                    const year = date.getUTCFullYear();
                    return `${day}/${month}/${year}`;
                  })()}
                </Badge>
              )}
              {filters.dateTo && (
                <Badge
                  variant="secondary"
                  className="bg-green-100 text-green-800"
                >
                  To:{' '}
                  {(() => {
                    // Extract date directly from ISO string to avoid timezone issues
                    const dateStr = filters.dateTo;
                    if (typeof dateStr === 'string' && dateStr.includes('T')) {
                      // ISO format: extract YYYY-MM-DD and parse
                      // For "To" date, we set it to end of day (23:59:59), so extract the date part
                      const [year, month, day] = dateStr
                        .split('T')[0]
                        .split('-')
                        .map(Number);
                      return `${String(day).padStart(2, '0')}/${String(
                        month
                      ).padStart(2, '0')}/${year}`;
                    }
                    // Fallback: parse as Date and use UTC
                    const date = new Date(dateStr);
                    const day = String(date.getUTCDate()).padStart(2, '0');
                    const month = String(date.getUTCMonth() + 1).padStart(
                      2,
                      '0'
                    );
                    const year = date.getUTCFullYear();
                    return `${day}/${month}/${year}`;
                  })()}
                </Badge>
              )}
              {filters.totalGte && (
                <Badge
                  variant="secondary"
                  className="bg-purple-100 text-purple-800"
                >
                  Min Total: ${filters.totalGte}
                </Badge>
              )}
              {filters.returned !== undefined && (
                <Badge
                  variant="secondary"
                  className="bg-orange-100 text-orange-800"
                >
                  {filters.returned ? 'Returned Only' : 'Not Returned'}
                </Badge>
              )}
              {filters.categoryId && (
                <Badge
                  variant="secondary"
                  className="bg-indigo-100 text-indigo-800"
                >
                  Category:{' '}
                  {categories.find((c) => c.id === filters.categoryId)?.name ||
                    filters.categoryId}
                </Badge>
              )}
              {filters.productIds && filters.productIds.length > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-teal-100 text-teal-800"
                >
                  {filters.productIds.length} Product
                  {filters.productIds.length > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Search */}
        <Card className="mb-4">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={translate('pos.orders.modern.search')}
                  value={searchValue}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === 'Enter' && handleSearch(searchValue)
                  }
                  className="pl-10"
                />
              </div>
              <Button onClick={() => handleSearch(searchValue)}>
                {translate('common.search', 'Search')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Status Tabs */}
        <div className="flex flex-wrap space-x-1 px-4 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleStatusFilter(tab.id)}
              className={`px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                filters.status === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {translate(tab.name)}
            </button>
          ))}
        </div>
      </div>

      {/* Orders Content - Responsive Layout */}
      <div className="mb-16">
        {/* Desktop Table View */}
        <div
          className={`${
            viewMode === 'auto'
              ? 'hidden lg:block'
              : viewMode === 'table'
              ? 'block'
              : 'hidden'
          } rounded-md border border-border bg-card shadow-sm overflow-x-auto`}
        >
          <div className="h-full flex flex-col min-w-0">
            <Table className="w-full">
              <TableHeader className="sticky top-0 z-10 bg-muted">
                <TableRow>
                  {visibleColumns.date && (
                    <TableHead
                      className="text-gray-700 font-semibold"
                      style={{ position: 'sticky', top: 0, zIndex: 10 }}
                    >
                      <button
                        onClick={() => handleSort('date')}
                        className="flex items-center gap-1 hover:text-gray-900"
                      >
                        {translate('pos.orders.fields.date')}
                        {getSortIcon('date')}
                      </button>
                    </TableHead>
                  )}
                  {visibleColumns.reference && (
                    <TableHead className="text-gray-700 font-semibold">
                      <button
                        onClick={() => handleSort('reference')}
                        className="flex items-center gap-1 hover:text-gray-900"
                      >
                        {translate('pos.orders.fields.reference')}
                        {getSortIcon('reference')}
                      </button>
                    </TableHead>
                  )}
                  {visibleColumns.customer && (
                    <TableHead className="text-gray-700 font-semibold">
                      {translate('pos.orders.fields.customer')}
                    </TableHead>
                  )}
                  {visibleColumns.address && (
                    <TableHead className="text-gray-700 font-semibold">
                      {translate('pos.orders.fields.address')}
                    </TableHead>
                  )}
                  {visibleColumns.status && (
                    <TableHead className="text-gray-700 font-semibold">
                      {translate('pos.orders.fields.status')}
                    </TableHead>
                  )}
                  {visibleColumns.items && (
                    <TableHead className="text-gray-700 font-semibold">
                      {translate('pos.orders.fields.items')}
                    </TableHead>
                  )}
                  {visibleColumns.note && (
                    <TableHead className="text-gray-700 font-semibold">
                      {translate('pos.orders.fields.note')}
                    </TableHead>
                  )}
                  {visibleColumns.subtotal && (
                    <TableHead className="text-gray-700 font-semibold">
                      {translate('pos.orders.fields.subtotal')}
                    </TableHead>
                  )}
                  {visibleColumns.discount && (
                    <TableHead className="text-gray-700 font-semibold">
                      {translate('pos.orders.fields.discount')}
                    </TableHead>
                  )}
                  {visibleColumns.total && (
                    <TableHead className="text-gray-700 font-semibold">
                      <button
                        onClick={() => handleSort('total')}
                        className="flex items-center gap-1 hover:text-gray-900"
                      >
                        {translate('pos.orders.fields.total')}
                        {getSortIcon('total')}
                      </button>
                    </TableHead>
                  )}
                  {filters.status === 'delivered' &&
                    visibleColumns.returned && (
                      <TableHead className="text-gray-700 font-semibold">
                        {translate('pos.orders.fields.returned')}
                      </TableHead>
                    )}
                  {visibleColumns.actions && (
                    <TableHead className="text-right text-gray-700 font-semibold">
                      {translate('pos.common.action.actions')}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody className="overflow-y-auto">
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={getVisibleColumnCount()}
                      className="text-center py-12"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <LoadingSpinner size="lg" variant="secondary" className="mb-2" />
                        <span className="text-gray-600">
                          {translate('pos.orders.modern.loading')}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : orders.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={getVisibleColumnCount()}
                      className="text-center py-12 text-gray-500"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <span className="text-lg">
                          {translate('pos.orders.modern.noOrders')}
                        </span>
                        <span className="text-sm text-gray-400">
                          {translate('pos.orders.modern.tryFilters')}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => (
                    <TableRow
                      key={order.id}
                      data-order-id={order.id}
                      className="order-row hover:bg-muted/50 border-b border-border"
                    >
                      {visibleColumns.date && (
                        <TableCell className="text-sm text-foreground">
                          <div className="font-medium">
                            {formatDate(order.date)}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.reference && (
                        <TableCell className="text-sm text-foreground">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium">{order.reference}</div>
                            {suborderCounts[order.id.toString()] > 0 && (
                              <Badge
                                variant="outline"
                                className="bg-blue-50 text-blue-700 border-blue-200 text-xs"
                                title={translate(
                                  'pos.orders.fields.suborders',
                                  'Suborders'
                                )}
                              >
                                {suborderCounts[order.id.toString()]}{' '}
                                {translate(
                                  'pos.orders.fields.suborders',
                                  'Suborders'
                                )}
                              </Badge>
                            )}
                            {(order as any).parentOrderId && (
                              <Badge
                                variant="outline"
                                className="bg-gray-50 text-gray-600 border-gray-200 text-xs"
                              >
                                {translate(
                                  'pos.orders.fields.suborder',
                                  'Suborder'
                                )}
                              </Badge>
                            )}
                            {(order as any).sourcePlatform === 'grab' && (
                              <Badge
                                variant="outline"
                                className="bg-green-50 text-green-700 border-green-200"
                              >
                                Grab
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.customer && (
                        <TableCell className="text-sm text-foreground">
                          {order.customerId ? (
                            <CustomerReferenceField
                              customerId={order.customerId}
                              showPoints={true}
                              showZalo={true}
                              sourcePlatform={(order as any).sourcePlatform}
                            />
                          ) : (order as any).sourcePlatform &&
                            (order as any).sourcePlatform !== 'internal' ? (
                            <Badge
                              variant="outline"
                              className="bg-green-50 text-green-700 border-green-200"
                            >
                              {(() => {
                                const platform = (
                                  order as any
                                ).sourcePlatform?.toLowerCase();
                                const platformMap: Record<string, string> = {
                                  grab: 'Grab',
                                  shopee: 'Shopee',
                                  xanhsm: 'Xanh SM',
                                  xanh_sm: 'Xanh SM',
                                };
                                return platformMap[platform] || platform;
                              })()}
                            </Badge>
                          ) : (
                            <span className="text-gray-500 italic">
                              {translate(
                                'pos.orders.modern.walkInCustomer'
                              )}
                            </span>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.address && (
                        <TableCell className="text-sm text-foreground">
                          {(() => {
                            // Check order type first to determine display
                            const orderType = order.orderType?.toLowerCase();

                            if (orderType === 'delivery') {
                              // For delivery orders, show customer address if available
                              if (order.customerId) {
                                return (
                                  <AddressField customerId={order.customerId} />
                                );
                              }
                              // If no customer, show "Delivery"
                              return (
                                <span className="text-muted-foreground italic">
                                  {translate(
                                    'pos.orders.orderTypes.delivery'
                                  )}
                                </span>
                              );
                            }

                            if (orderType === 'takeaway') {
                              // For takeaway orders, show "Takeaway"
                              return (
                                <span className="text-muted-foreground italic">
                                  {translate(
                                    'pos.orders.orderTypes.takeaway'
                                  )}
                                </span>
                              );
                            }

                            // For dine-in orders (default or explicit)
                            if (order.customerId) {
                              // If customer exists, show their address
                              return (
                                <AddressField customerId={order.customerId} />
                              );
                            }

                            // Default to dine-in display with table number if available
                            return (
                              <span className="text-muted-foreground italic">
                                {order.tableNumber
                                  ? translate(
                                      'pos.orders.modern.dineInTable',
                                      {
                                        tableNumber: String(order.tableNumber),
                                      }
                                    )
                                  : translate(
                                      'pos.orders.modern.dineIn'
                                    )}
                              </span>
                            );
                          })()}
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell className="text-sm text-foreground">
                          <Badge
                            className={
                              order.status === 'ordered'
                                ? 'bg-blue-100 text-blue-800'
                                : order.status === 'ready'
                                ? 'bg-yellow-100 text-yellow-800'
                                : order.status === 'delivered'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }
                          >
                            {(() => {
                              const translationKey = `pos.orders.tabs.${order.status}`;
                              const translated = translate(translationKey);
                              // Fallback if translation fails
                              if (
                                translated === translationKey ||
                                translated.startsWith('pos.')
                              ) {
                                const fallbackTranslations: Record<
                                  OrderStatus,
                                  string
                                > = {
                                  ordered: 'Đã đặt',
                                  ready: 'Sẵn sàng',
                                  delivered: 'Đã giao',
                                  cancelled: 'Đã hủy',
                                };
                                return (
                                  fallbackTranslations[order.status] ||
                                  order.status
                                );
                              }
                              return translated;
                            })()}
                          </Badge>
                        </TableCell>
                      )}
                      {visibleColumns.items && (
                        <TableCell className="text-sm text-foreground">
                          {order.items && Array.isArray(order.items) ? (
                            <button
                              onClick={() => handleProductSummaryClick(order)}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {(() => {
                                const parentItemCount = order.items.reduce(
                                  (sum, item) => sum + (item.quantity || 0),
                                  0
                                );
                                // For parent orders, we could show combined count, but we don't have suborder items here
                                // So just show parent count for now
                                return parentItemCount;
                              })()}{' '}
                              {translate('pos.orders.modern.items')}
                              {suborderCounts[order.id.toString()] > 0 && (
                                <span className="text-xs text-gray-500 ml-1">
                                  (+{suborderCounts[order.id.toString()]}{' '}
                                  suborders)
                                </span>
                              )}
                            </button>
                          ) : (
                            <NbItemsField />
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.note && (
                        <TableCell className="text-sm text-foreground">
                          {(() => {
                            // Get notes from items
                            const itemNotes = order.items
                              ?.filter((item) => item.note)
                              .map((item) => item.note)
                              .filter(Boolean);

                            if (itemNotes && itemNotes.length > 0) {
                              return (
                                <div className="space-y-1 max-w-[300px]">
                                  {itemNotes.map((note, index) => (
                                    <div
                                      key={index}
                                      className="text-muted-foreground text-xs truncate"
                                      title={note}
                                    >
                                      • {note}
                                    </div>
                                  ))}
                                </div>
                              );
                            } else {
                              return (
                                <span className="text-muted-foreground italic">
                                  {translate(
                                    'pos.orders.modern.noNotes'
                                  )}
                                </span>
                              );
                            }
                          })()}
                        </TableCell>
                      )}
                      {visibleColumns.subtotal && (
                        <TableCell className="text-sm text-foreground">
                          <div className="font-medium text-gray-700">
                            <span data-sensitive-field="amount">
                              {formatCurrency(calculateOrderSubtotal(order))}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.discount && (
                        <TableCell className="text-sm text-foreground">
                          <div className="font-medium text-orange-600">
                            <span data-sensitive-field="amount">
                              {formatCurrency(calculateOrderDiscount(order))}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.total && (
                        <TableCell className="text-sm text-foreground">
                          <div className="font-bold text-green-600">
                            <div>
                              <span data-sensitive-field="amount">
                                {formatCurrency(calculateOrderTotal(order))}
                              </span>
                            </div>
                            {suborderTotals[order.id.toString()] > 0 && (
                              <div className="text-xs text-gray-500 font-normal mt-0.5">
                                <span data-sensitive-field="amount">
                                  Combined:{' '}
                                  {formatCurrency(
                                    calculateOrderTotal(order) +
                                      suborderTotals[order.id.toString()]
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {filters.status === 'delivered' &&
                        visibleColumns.returned && (
                          <TableCell className="text-sm text-gray-900">
                            {order.returned && (
                              <Badge
                                variant="destructive"
                                className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs"
                              >
                                {translate(
                                  'pos.orders.modern.returned'
                                )}
                              </Badge>
                            )}
                          </TableCell>
                        )}
                      {visibleColumns.actions && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Compact Status Buttons */}
                            <div className="flex items-center gap-1">
                              <Button
                                variant={
                                  order.status === 'ordered'
                                    ? 'default'
                                    : 'outline'
                                }
                                size="sm"
                                className={`h-7 px-2 text-xs ${
                                  order.status === 'ordered'
                                    ? 'bg-blue-600 text-white'
                                    : 'border-border text-foreground hover:bg-blue-50 dark:hover:bg-blue-900'
                                }`}
                                onClick={() =>
                                  handleStatusUpdate(
                                    order.id.toString(),
                                    'ordered'
                                  )
                                }
                                disabled={
                                  !canUpdateOrder(order) ||
                                  order.status === 'ordered'
                                }
                                title={translate(
                                  'pos.orders.tabs.ordered'
                                )}
                                data-testid="mark-ordered-button"
                              >
                                {order.status === 'ordered' ? '✓' : 'O'}
                              </Button>
                              <Button
                                variant={
                                  order.status === 'ready'
                                    ? 'default'
                                    : 'outline'
                                }
                                size="sm"
                                className={`h-7 px-2 text-xs ${
                                  order.status === 'ready'
                                    ? 'bg-yellow-600 text-white'
                                    : 'border-border text-foreground hover:bg-yellow-50 dark:hover:bg-yellow-900'
                                }`}
                                onClick={() =>
                                  handleStatusUpdate(
                                    order.id.toString(),
                                    'ready'
                                  )
                                }
                                disabled={!canUpdateOrder(order)}
                                title={translate(
                                  'pos.orders.tabs.ready'
                                )}
                                data-testid="mark-ready-button"
                              >
                                {order.status === 'ready' ? '✓' : 'R'}
                              </Button>
                              <Button
                                variant={
                                  order.status === 'delivered'
                                    ? 'default'
                                    : 'outline'
                                }
                                size="sm"
                                className={`h-7 px-2 text-xs ${
                                  order.status === 'delivered'
                                    ? 'bg-green-600 text-white'
                                    : 'border-border text-foreground hover:bg-green-50 dark:hover:bg-green-900'
                                }`}
                                onClick={() =>
                                  handleStatusUpdate(
                                    order.id.toString(),
                                    'delivered'
                                  )
                                }
                                disabled={!canUpdateOrder(order)}
                                title={translate(
                                  'pos.orders.tabs.delivered'
                                )}
                                data-testid="mark-delivered-button"
                              >
                                {order.status === 'delivered' ? '✓' : 'D'}
                              </Button>
                              <Button
                                variant={
                                  order.status === 'cancelled'
                                    ? 'default'
                                    : 'outline'
                                }
                                size="sm"
                                className={`h-7 px-2 text-xs ${
                                  order.status === 'cancelled'
                                    ? 'bg-red-600 text-white'
                                    : 'border-border text-foreground hover:bg-red-50 dark:hover:bg-red-900'
                                }`}
                                onClick={() =>
                                  handleStatusUpdate(
                                    order.id.toString(),
                                    'cancelled'
                                  )
                                }
                                disabled={!canUpdateOrder(order)}
                                title={translate(
                                  'pos.orders.tabs.cancelled'
                                )}
                              >
                                {order.status === 'cancelled' ? '✗' : 'C'}
                              </Button>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() =>
                                handleEditOrder(order.id.toString())
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() =>
                                handleViewOrder(order.id.toString())
                              }
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div
          className={`${
            viewMode === 'auto'
              ? 'lg:hidden'
              : viewMode === 'cards'
              ? 'block'
              : 'hidden'
          } px-4`}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <LoadingSpinner size="lg" variant="secondary" className="mb-2" />
              <span className="text-gray-600">
                {translate('pos.orders.modern.loading')}
              </span>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="flex flex-col items-center justify-center">
                <span className="text-lg">
                  {translate('pos.orders.modern.noOrders')}
                </span>
                <span className="text-sm text-gray-400">
                  {translate('pos.orders.modern.tryFilters')}
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <MobileOrderCard
                  key={order.id}
                  order={order}
                  onStatusUpdate={handleStatusUpdate}
                  onEdit={handleEditOrder}
                  onView={handleViewOrder}
                  onProductSummaryClick={handleProductSummaryClick}
                  showReturnedColumn={filters.status === 'delivered'}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sticky Pagination at Bottom */}
      {pagination.total > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-20">
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 gap-4">
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              {translate('pos.orders.modern.pagination.showing', {
                start: (pagination.page - 1) * pagination.perPage + 1,
                end: Math.min(
                  pagination.page * pagination.perPage,
                  pagination.total
                ),
                total: pagination.total,
              })}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate('pos.orders.modern.pagination.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={
                  pagination.page * pagination.perPage >= pagination.total
                }
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate('pos.orders.modern.pagination.next')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {orders.length === 0 && !loading && (
        <div className="text-center py-12 bg-card rounded-lg shadow-sm border border-border">
          <p className="text-muted-foreground">
            {translate('pos.orders.modern.noOrders')}
          </p>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-card-foreground">
                {translate('pos.orders.modern.filters')}
              </h3>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowFilterModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Customer Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {translate('pos.orders.fields.customer')}
                </label>
                <Input
                  placeholder="Search customer..."
                  value={filterForm.customerId}
                  onChange={(e) =>
                    handleFilterChange('customerId', e.target.value)
                  }
                />
              </div>

              {/* Category Filter */}
              <div>
                <Label className="block text-sm font-medium text-foreground mb-1">
                  {translate(
                    'pos.products.fields.categoryId',
                    'Category'
                  )}
                </Label>
                <Select
                  value={filterForm.categoryId || 'all'}
                  onValueChange={(value) =>
                    handleFilterChange('categoryId', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={translate('common.all', 'All')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {translate('common.all', 'All')}
                    </SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Products Filter */}
              <div>
                <Label className="block text-sm font-medium text-foreground mb-1">
                  {translate('pos.products.name', 'Products')}
                </Label>
                <Input
                  placeholder={translate(
                    'pos.campaigns.search_products',
                    'Search products...'
                  )}
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  className="mb-2"
                />
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {loadingProducts ? (
                    <div className="text-sm text-muted-foreground">
                      {translate(
                        'pos.campaigns.loading_products',
                        'Loading products...'
                      )}
                    </div>
                  ) : products.filter((p) =>
                      productSearchTerm
                        ? p.name
                            ?.toLowerCase()
                            .includes(productSearchTerm.toLowerCase())
                        : true
                    ).length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      {translate(
                        'pos.campaigns.no_products',
                        'No products found'
                      )}
                    </div>
                  ) : (
                    products
                      .filter((p) =>
                        productSearchTerm
                          ? p.name
                              ?.toLowerCase()
                              .includes(productSearchTerm.toLowerCase())
                          : true
                      )
                      .map((product) => (
                        <div
                          key={product.id}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`filter-product-${product.id}`}
                            checked={filterForm.productIds?.includes(
                              product.id
                            )}
                            onCheckedChange={() =>
                              handleProductToggle(product.id)
                            }
                          />
                          <Label
                            htmlFor={`filter-product-${product.id}`}
                            className="text-sm font-normal cursor-pointer flex-1"
                          >
                            {product.name}
                          </Label>
                        </div>
                      ))
                  )}
                </div>
                {filterForm.productIds && filterForm.productIds.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {filterForm.productIds.map((prodId) => {
                      const product = products.find((p) => p.id === prodId);
                      return product ? (
                        <Badge
                          key={prodId}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {product.name}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => handleProductToggle(prodId)}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              {/* Date Range */}
              <div>
                <QuickDateRange
                  selectedPreset={selectedPreset}
                  onRangeChange={(from, to, preset) => {
                    setSelectedPreset(preset);
                    if (preset !== 'custom') {
                      setFilterForm((prev) => ({
                        ...prev,
                        dateFrom: from,
                        dateTo: to,
                      }));
                    }
                  }}
                  className="mb-4"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {translate('pos.orders.filters.dateGte')}
                  </label>
                  <Input
                    type="date"
                    value={filterForm.dateFrom}
                    onChange={(e) => {
                      handleFilterChange('dateFrom', e.target.value);
                      setSelectedPreset('custom');
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {translate('pos.orders.filters.dateLte')}
                  </label>
                  <Input
                    type="date"
                    value={filterForm.dateTo}
                    onChange={(e) => {
                      handleFilterChange('dateTo', e.target.value);
                      setSelectedPreset('custom');
                    }}
                  />
                </div>
              </div>

              {/* Total Amount */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {translate('pos.orders.filters.totalGte')}
                </label>
                <Input
                  type="number"
                  placeholder="Minimum total amount"
                  value={filterForm.totalGte}
                  onChange={(e) =>
                    handleFilterChange('totalGte', e.target.value)
                  }
                />
              </div>

              {/* Returned Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {translate('pos.orders.fields.returned')}
                </label>
                <select
                  value={filterForm.returned}
                  onChange={(e) =>
                    handleFilterChange('returned', e.target.value)
                  }
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
                  aria-label={translate('pos.orders.fields.returned')}
                >
                  <option value="">All orders</option>
                  <option value="true">Returned only</option>
                  <option value="false">Not returned</option>
                </select>
              </div>

              {/* Platform Filter */}
              <div>
                <Label className="block text-sm font-medium text-foreground mb-1">
                  {translate(
                    'pos.orders.fields.deliveryPlatform',
                    'Platform'
                  )}
                </Label>
                <Select
                  value={filterForm.sourcePlatform || 'all'}
                  onValueChange={(value) =>
                    handleFilterChange('sourcePlatform', value === 'all' ? '' : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={translate('common.all', 'All')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {translate('common.all', 'All')}
                    </SelectItem>
                    <SelectItem value="internal">
                      {translate(
                        'pos.orders.deliveryPlatforms.internal',
                        'Internal'
                      )}
                    </SelectItem>
                    <SelectItem value="grab">
                      {translate(
                        'pos.orders.deliveryPlatforms.grab',
                        'Grab'
                      )}
                    </SelectItem>
                    <SelectItem value="shopee">
                      {translate(
                        'pos.orders.deliveryPlatforms.shopee',
                        'Shopee'
                      )}
                    </SelectItem>
                    <SelectItem value="xanhsm">
                      {translate(
                        'pos.orders.deliveryPlatforms.xanhsm',
                        'Xanh SM'
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                onClick={handleClearFilters}
                className="flex-1"
              >
                {translate('pos.common.action.clear')}
              </Button>
              <Button
                onClick={handleApplyFilters}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {translate('pos.common.action.apply')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Product Summary Dialog */}
      <ProductSummaryDialog
        order={selectedOrder}
        isOpen={showProductDialog}
        onClose={handleCloseProductDialog}
      />

      {/* Feature Wizard - DISABLED */}
      {/* <FeatureWizard
        open={isWizardOpen}
        onClose={closeWizard}
        onComplete={completeWizard}
        onSkip={skipWizard}
        title={wizardConfig.title}
        description={wizardConfig.description}
        featureId={wizardConfig.id}
        steps={getModernOrderListWizardSteps(translate)}
      /> */}
    </div>
  );
};

export default ModernOrderList;
